import ViverseAvatar from '@/components/ViverseAvatar'
import { Comment } from '@/types/comments'
import { useTranslations } from 'next-intl'
import { ReactNode, useMemo } from 'react'

function formatTimeAgo(timeString: string) {
  const unixTime = Date.parse(timeString)
  if (typeof unixTime === 'number') {
    const ts = unixTime < 1e12 ? unixTime * 1000 : unixTime
    const diff = Date.now() - ts

    const MIN = 60_000,
      HOUR = 3_600_000,
      DAY = 86_400_000,
      WEEK = 7 * DAY,
      YEAR = 365 * DAY,
      MONTH = YEAR / 12

    const table = [
      { limit: MIN, unit: null, unitKey: 'now' },
      { limit: HOUR, unit: MIN, unitKey: 'minute' },
      { limit: DAY, unit: HOUR, unitKey: 'hour' },
      { limit: WEEK, unit: DAY, unitKey: 'day' },
      { limit: 4 * WEEK, unit: WEEK, unitKey: 'week' },
      { limit: YEAR, unit: MONTH, unitKey: 'month' },
      { limit: Infinity, unit: YEAR, unitKey: 'year' },
    ]

    for (const row of table) {
      if (diff < row.limit) {
        return { time: `${row.unit ? Math.floor(diff / row.unit) : ''}`, unitKey: row.unitKey }
      }
    }
  }
  return { time: '', unitKey: '' }
}

interface CommentTitleProps {
  children?: ReactNode
  isTablet?: boolean
  isRemoved?: boolean
  comment: Comment
}

function CommentTitle({ children, isTablet, isRemoved = false, comment }: CommentTitleProps) {
  const t = useTranslations()
  const commentTimeAgo = useMemo(() => formatTimeAgo(comment.date_created), [comment.date_created])

  const renderTimeDisplay = () => {
    const timeValue = commentTimeAgo.time
    const timeUnit = t(`comment-title.time-unit.${commentTimeAgo.unitKey}`)
    const ageSuffix = commentTimeAgo.unitKey === 'now' ? '' : t('comment-title.age')

    return `${timeValue}${timeUnit} ${ageSuffix}`.trim()
  }

  return (
    <div className="mb-2 flex h-8.5 w-full gap-2 md:mb-0 md:gap-4">
      <ViverseAvatar
        size={isTablet ? 'M' : 'S'}
        imageUrl={isRemoved ? '' : comment.owner.head_icon_url}
      />
      <div className="flex flex-1 items-center gap-1 md:gap-2">
        <span className="subheader4-omni-styles text-vive-primary-20">
          {isRemoved ? t('comment-title.deleted') : `@${comment.owner.display_name}`}
        </span>
        <span className="text-vive-primary-40 text-xs leading-4 font-normal tracking-[0.15px]">
          {renderTimeDisplay()}
        </span>
      </div>
      {isRemoved ? <div /> : children}
    </div>
  )
}

export default CommentTitle
